// frontend/src/pages/Person.jsx
// ============================================================================
// IMPROVED VERSION - Google/AWS Style
// ============================================================================
// Changes:
// 1. Added breadcrumb navigation
// 2. Visual grouping in Overview (Identity / Contact / Compliance)
// 3. Country dropdown instead of ISO2 text input
// 4. Document expiry warning badge on Documents tab
// 5. Fallback document kinds if API returns empty
// ============================================================================

import { useEffect, useMemo, useState, Fragment } from "react";
import { useParams, Link } from "react-router-dom";
import ErrorBoundary from "../components/ErrorBoundary.jsx";
import {
  updatePerson,
  getPerson,
  getPersonRoles,
  getPersonDocs,
  getPersonAddresses,
  getPersonNotes,
  addPersonNote,
  updatePersonNote,
  deletePersonNote,
  addPersonDoc,
  updatePersonDoc,
  deletePersonDoc,
  addPersonAddress,
  updatePersonAddress,
  deletePersonAddress,
  getPersonStatusHistory,
  getDocRequirements,
  downloadPersonDoc,
  getPersonDocSignedUrl,
  openDocumentWithSignedUrl,
  getCountries,
  getDocStatuses,
  setPersonStatus,
  softDeletePerson,
  restorePerson,
  purgePerson,
} from "../api";

import countriesFallback from "../lib/countriesFallback.js";
import { useNotify } from "../components/Notify.jsx";

/* --------------------------- small UI helpers --------------------------- */
const Tab = ({ id, active, onClick, children, badge }) => (
  <button className={`tab-btn ${active ? "active" : ""}`} onClick={() => onClick(id)} type="button">
    {children}
    {badge && <span className="tab-badge">{badge}</span>}
  </button>
);

const Field = ({ label, children }) => (
  <label className="f-field">
    <div className="f-label">{label}</div>
    {children}
  </label>
);

const FieldGroup = ({ title, children }) => (
  <div className="field-group">
    <h4 className="field-group-title">{title}</h4>
    <div className="form-grid">{children}</div>
  </div>
);

function CountrySelect({ value, onChange, countries, placeholder = "—" }) {
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {countries.map((c) => (
        <option key={c.id || c.iso2} value={String(c.id || "")}>
          {c.name}
          {c.iso2 ? ` (${String(c.iso2).toUpperCase()})` : ""}
        </option>
      ))}
    </select>
  );
}

/* ------------------------------- utilities ------------------------------- */
const titleCase = (input) => {
  const s = (input ?? "")
    .toString()
    .replace(/[_\-]+/g, " ")
    .trim();
  if (!s) return "—";
  return s
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ""))
    .join(" ");
};

// Helper to count expiring documents
const countExpiringDocs = (docs) => {
  const now = new Date();
  const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  return docs.filter((d) => {
    if (!d.expires_on) return false;
    const expiryDate = new Date(d.expires_on);
    return expiryDate > now && expiryDate <= thirtyDaysFromNow;
  }).length;
};

/* ================================= page ================================= */
export default function PersonPage() {
  const { id } = useParams();
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const notify = useNotify();

  const [person, setPerson] = useState(null);
  const [roles, setRoles] = useState([]);
  const [docs, setDocs] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [notes, setNotes] = useState([]);
  const [statusHistory, setStatusHistory] = useState([]);

  const [editDoc, setEditDoc] = useState(null);
  const [editAddress, setEditAddress] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteDraft, setNoteDraft] = useState({ note: "", source: "" });

  const [meta, setMeta] = useState({ docStatuses: [] });

  // Countries
  const [countries, setCountries] = useState([]);
  const countryById = useMemo(() => {
    const m = new Map();
    countries.forEach((c) => {
      if (c.id != null) {
        const numId = Number(c.id);
        const strId = String(c.id);
        m.set(numId, c);
        m.set(strId, c);
      }
    });
    return m;
  }, [countries]);

  // Fallback document kinds for individuals
  const FALLBACK_DOC_KINDS = [
    { key: "passport", value: "passport", label: "Passport" },
    { key: "id", value: "id", label: "ID Card" },
    { key: "proof_of_address", value: "proof_of_address", label: "Proof of Address" },
    { key: "other", value: "other", label: "Other" },
  ];

  // Individual (UBO) doc requirements from backend
  const [reqIndividual, setReqIndividual] = useState([]);

  // Use fallback if no requirements loaded
  const effectiveDocKinds = useMemo(() => {
    return reqIndividual.length > 0 ? reqIndividual : FALLBACK_DOC_KINDS;
  }, [reqIndividual]);

  // Form states
  const [profile, setProfile] = useState({
    first_name: "",
    last_name: "",
    full_name: "",
    date_of_birth: "",
    place_of_birth: "",
    nationality: "",
    country_id: "", // ✅ Changed from country_iso2
    email: "",
    phone: "",
    pep: "",
    id_number: "",
    passport_number: "",
    tax_number: "",
  });

  const [newDoc, setNewDoc] = useState({
    kind: "",
    other_kind: "",
    identifier: "",
    issued_on: "",
    expires_on: "",
    note: "",
    file: null,
  });

  const [newAddress, setNewAddress] = useState({
    type: "residence",
    address_text: "",
    country_id: "",
  });

  const [newNote, setNewNote] = useState({ note: "", source: "" });

  useEffect(() => {
    let cancelled = false;
    async function loadMeta() {
      const [statuses] = await Promise.all([getDocStatuses().catch(() => [])]);
      if (!cancelled) setMeta({ docStatuses: statuses });
    }
    loadMeta();
    return () => {
      cancelled = true;
    };
  }, []);

  async function loadAll() {
    setLoading(true);
    setErr("");
    try {
      const p = await getPerson(id);
      setPerson(p);

      const [r, d, addr, nt, sh, reqInd, countriesList] = await Promise.all([
        getPersonRoles(id),
        getPersonDocs(id),
        getPersonAddresses(id),
        getPersonNotes(id),
        getPersonStatusHistory(id),
        getDocRequirements({ client_type: "individual" }).catch(() => []),
        getCountries().catch(() => []),
      ]);

      setRoles(r);
      setDocs(d);
      setAddresses(addr);
      setNotes(nt);
      setStatusHistory(sh);
      setReqIndividual(reqInd);

      // Countries with fallback merge
      if (!countriesList || !Array.isArray(countriesList) || countriesList.length < 200) {
        const byIso = new Map();
        (Array.isArray(countriesList) ? countriesList : []).forEach((c) => {
          if (!c || !c.iso2) return;
          byIso.set(String(c.iso2).toUpperCase(), {
            id: c.id != null ? Number(c.id) : null,
            name: c.name || "",
            iso2: String(c.iso2).toUpperCase(),
          });
        });
        countriesFallback.forEach((country, idx) => {
          const iso = String(country.iso2).toUpperCase();
          if (byIso.has(iso)) return;
          byIso.set(iso, { id: 10000 + idx, name: country.name, iso2: iso });
        });
        const merged = [...byIso.values()].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setCountries(merged);
      } else {
        const normalizedList = countriesList
          .map((c) => ({ id: c.id != null ? Number(c.id) : null, name: c.name || "", iso2: (c.iso2 || "").toUpperCase() }))
          .filter((c) => c.name && c.id != null)
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setCountries(normalizedList);
      }

      // ✅ Map country_id from database to form
      setProfile({
        first_name: p.first_name || "",
        last_name: p.last_name || "",
        full_name: p.full_name || "",
        date_of_birth: p.date_of_birth ? p.date_of_birth.slice(0, 10) : "",
        place_of_birth: p.place_of_birth || "",
        nationality: p.nationality || "",
        country_id: p.country_id ? String(p.country_id) : "", // ✅ Use country_id
        email: p.email || "",
        phone: p.phone || "",
        pep: p.pep || "",
        id_number: p.id_number || "",
        passport_number: p.passport_number || "",
        tax_number: p.tax_number || "",
      });
    } catch (e) {
      setErr(e.message || "Person not found");
      setPerson(null);
      setRoles([]);
      setDocs([]);
      setAddresses([]);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }

  // load person + related
  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Keep newDoc.kind sane as requirements load/change
  useEffect(() => {
    if (effectiveDocKinds?.length) {
      const keys = new Set(effectiveDocKinds.map((x) => x.key || x.value));
      if (!keys.has(newDoc.kind)) {
        setNewDoc((d) => ({ ...d, kind: effectiveDocKinds[0].key || effectiveDocKinds[0].value }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveDocKinds]);

  const title = useMemo(() => {
    if (!person) return `Person ${id}`;
    const name = person.full_name || [person.first_name, person.last_name].filter(Boolean).join(" ");
    return name || `Person ${id}`;
  }, [person, id]);

  // ✅ Count expiring documents
  const expiringCount = useMemo(() => countExpiringDocs(docs), [docs]);

  // open file via signed URL
  async function openDocWithSignedUrl(docId) {
    try {
      await openDocumentWithSignedUrl(() => getPersonDocSignedUrl(id, docId), {
        notify,
        fallbackDownload: () => downloadPersonDoc(id, docId),
      });
    } catch (e) {
      notify(e.message || "Could not open document");
    }
  }

  /* -------------------------------- actions -------------------------------- */
  async function saveProfile(e) {
    e.preventDefault();
    try {
      // ✅ Convert country_id back to number
      await updatePerson(id, {
        ...profile,
        country_id: profile.country_id ? Number(profile.country_id) : null,
        date_of_birth: profile.date_of_birth || null,
        pep: profile.pep || null,
      });
      await loadAll();
      notify("Person details saved");
    } catch (e2) {
      notify(e2.message);
    }
  }

  async function addDoc(e) {
    e.preventDefault();
    try {
      await addPersonDoc(id, newDoc);
      setNewDoc({
        kind: effectiveDocKinds[0]?.key || effectiveDocKinds[0]?.value || "",
        other_kind: "",
        identifier: "",
        issued_on: "",
        expires_on: "",
        note: "",
        file: null,
      });
      await loadAll();
    } catch (e2) {
      notify(e2.message);
    }
  }

  function startEditDocRow(d) {
    setEditDoc({
      id: d.id,
      kind: d.kind,
      other_kind: "",
      identifier: d.identifier || "",
      issued_on: d.issued_on ? d.issued_on.slice(0, 10) : "",
      expires_on: d.expires_on ? d.expires_on.slice(0, 10) : "",
      status: d.status || "",
      note: d.note || "",
      file: null,
      path: d.path || null,
    });
  }

  async function saveDocEdit(e) {
    e?.preventDefault?.();
    try {
      await updatePersonDoc(id, editDoc.id, editDoc);
    } catch (e2) {
      notify(e2.message);
    } finally {
      setEditDoc(null);
      await loadAll();
    }
  }

  async function removeDoc(docId) {
    if (!confirm("Delete document?")) return;
    try {
      await deletePersonDoc(id, docId);
      await loadAll();
    } catch (e2) {
      notify(e2.message);
    }
  }

  function startEditAddress(a) {
    setEditAddress({
      id: a.id,
      type: a.type || "residence",
      address_text: a.address_text || "",
      country_id: a.country_id ? String(a.country_id) : "",
    });
  }

  async function saveAddressEdit(e) {
    e?.preventDefault?.();
    try {
      await updatePersonAddress(id, editAddress.id, {
        type: editAddress.type,
        address_text: editAddress.address_text,
        country_id: editAddress.country_id ? Number(editAddress.country_id) : null,
      });
    } catch (err) {
      notify(err.message);
    } finally {
      setEditAddress(null);
      await loadAll();
    }
  }

  async function removeAddress(addrId) {
    if (!confirm("Delete this address?")) return;
    try {
      await deletePersonAddress(id, addrId);
      await loadAll();
    } catch (err) {
      notify(err.message);
    }
  }

  async function addAddress(e) {
    e.preventDefault();
    try {
      const countryId = newAddress.country_id ? Number(newAddress.country_id) : null;
      await addPersonAddress(id, { type: newAddress.type, address_text: newAddress.address_text, country_id: countryId });
      setNewAddress({ type: "residence", address_text: "", country_id: "" });
      await loadAll();
    } catch (e2) {
      notify(e2.message);
    }
  }

  async function addNote(e) {
    e.preventDefault();
    try {
      await addPersonNote(id, newNote);
      setNewNote({ note: "", source: "" });
      await loadAll();
    } catch (e2) {
      notify(e2.message);
    }
  }

  async function changeStatus(e) {
    const next = e.target.value;
    try {
      await setPersonStatus(id, next);
      await loadAll();
    } catch (err) {
      notify(err.message);
    }
  }

  function beginEditNote(n) {
    setEditingNoteId(n.id);
    setNoteDraft({ note: n.note || "", source: n.source || "" });
  }

  async function saveNoteEdit() {
    try {
      await updatePersonNote(id, editingNoteId, noteDraft);
      setEditingNoteId(null);
      setNoteDraft({ note: "", source: "" });
      await loadAll();
    } catch (e2) {
      notify(e2.message);
    }
  }

  async function removeNote(noteId) {
    if (!confirm("Delete note?")) return;
    try {
      await deletePersonNote(id, noteId);
      await loadAll();
    } catch (e2) {
      notify(e2.message);
    }
  }

  if (loading) {
    return (
      <ErrorBoundary componentName="PersonPage">
        <div className="container">
          <p>Loading…</p>
        </div>
      </ErrorBoundary>
    );
  }
  if (err) {
    return (
      <ErrorBoundary componentName="PersonPage">
        <div className="container">
          <p className="error">{err}</p>
        </div>
      </ErrorBoundary>
    );
  }
  if (!person) {
    return (
      <ErrorBoundary componentName="PersonPage">
        <div className="container">
          <p>Not found</p>
        </div>
      </ErrorBoundary>
    );
  }

  /* ---------------------------------- UI ---------------------------------- */
  return (
    <ErrorBoundary componentName="PersonPage">
      <div className="container">
        {/* ✅ Breadcrumb Navigation */}
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <Link to="/search">Search</Link>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-current">{title}</span>
        </nav>

        <div className="page-top">
          <Link to="/search" className="back-link" aria-label="Back to compliance search">
            <span aria-hidden="true">←</span> Back
          </Link>

          <h1 className="page-title">{person.full_name || [person.first_name, person.last_name].filter(Boolean).join(" ") || `Person ${id}`}</h1>
          <div className={`badge status-${person.status}`}>{person.status}</div>

          <select value={person.status} onChange={changeStatus} style={{ marginLeft: 8 }} title="Change status">
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="liquidated">Liquidated</option>
            <option value="deceased">Deceased</option>
            <option value="deleted">Deleted</option>
          </select>

          <button
            type="button"
            className="danger"
            style={{ marginLeft: 8 }}
            onClick={async () => {
              if (!confirm("Move this person to Trash (soft delete)?")) return;
              const reason = prompt("Optional reason for deletion (will be logged):", "") || "";
              try {
                await softDeletePerson(id, reason);
                await loadAll();
                notify("Moved to Trash.");
              } catch (e2) {
                notify(e2.message);
              }
            }}>
            Delete
          </button>

          {person.status === "deleted" && (
            <>
              <button
                type="button"
                className="secondary"
                style={{ marginLeft: 8 }}
                onClick={async () => {
                  try {
                    await restorePerson(id);
                    await loadAll();
                    notify("Restored.");
                  } catch (e2) {
                    notify(e2.message);
                  }
                }}>
                Restore
              </button>

              <button
                type="button"
                className="danger"
                style={{ marginLeft: 8 }}
                onClick={async () => {
                  const a = prompt("Type PURGE to permanently delete this person and all related data.");
                  if (a !== "PURGE") return;
                  try {
                    await purgePerson(id);
                    notify("Permanently deleted.");
                    window.history.back();
                  } catch (e2) {
                    notify(e2.message);
                  }
                }}>
                Purge
              </button>
            </>
          )}
          <div className="spacer" />
        </div>

        <div className="tabs" role="tablist" aria-label="Person sections">
          <Tab id="overview" active={tab === "overview"} onClick={setTab}>
            Overview
          </Tab>
          <Tab id="roles" active={tab === "roles"} onClick={setTab}>
            Company Roles
          </Tab>
          <Tab id="documents" active={tab === "documents"} onClick={setTab} badge={expiringCount > 0 ? `${expiringCount} expiring` : null}>
            Documents
          </Tab>
          <Tab id="addresses" active={tab === "addresses"} onClick={setTab}>
            Addresses
          </Tab>
          <Tab id="notes" active={tab === "notes"} onClick={setTab}>
            Notes
          </Tab>
        </div>

        {/* OVERVIEW - ✅ WITH VISUAL GROUPING */}
        {tab === "overview" && (
          <form className="card form-section" onSubmit={saveProfile}>
            <h3>Personal Information</h3>

            {/* ✅ Identity Section */}
            <FieldGroup title="Identity">
              <Field label="First name">
                <input value={profile.first_name} onChange={(e) => setProfile({ ...profile, first_name: e.target.value })} />
              </Field>
              <Field label="Last name">
                <input value={profile.last_name} onChange={(e) => setProfile({ ...profile, last_name: e.target.value })} />
              </Field>
              <Field label="Full name">
                <input value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} />
              </Field>
              <Field label="Date of birth">
                <input type="date" value={profile.date_of_birth} onChange={(e) => setProfile({ ...profile, date_of_birth: e.target.value })} />
              </Field>
              <Field label="Place of birth">
                <input value={profile.place_of_birth} onChange={(e) => setProfile({ ...profile, place_of_birth: e.target.value })} />
              </Field>
              <Field label="Nationality">
                <input value={profile.nationality} onChange={(e) => setProfile({ ...profile, nationality: e.target.value })} />
              </Field>
            </FieldGroup>

            {/* ✅ Contact Section */}
            <FieldGroup title="Contact Information">
              <Field label="Country">
                <CountrySelect value={profile.country_id} onChange={(val) => setProfile({ ...profile, country_id: val })} countries={countries} />
              </Field>
              <Field label="Email">
                <input value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} />
              </Field>
              <Field label="Phone">
                <input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
              </Field>
            </FieldGroup>

            {/* ✅ Compliance Section */}
            <FieldGroup title="Compliance Information">
              <Field label="PEP Status">
                <select value={profile.pep || ""} onChange={(e) => setProfile({ ...profile, pep: e.target.value })}>
                  <option value="">—</option>
                  <option value="Y">Y</option>
                  <option value="N">N</option>
                </select>
              </Field>
              <Field label="ID Number">
                <input value={profile.id_number} onChange={(e) => setProfile({ ...profile, id_number: e.target.value })} />
              </Field>
              <Field label="Passport Number">
                <input value={profile.passport_number} onChange={(e) => setProfile({ ...profile, passport_number: e.target.value })} />
              </Field>
              <Field label="Tax Number">
                <input value={profile.tax_number} onChange={(e) => setProfile({ ...profile, tax_number: e.target.value })} />
              </Field>
            </FieldGroup>

            <div className="form-actions">
              <button type="submit">Save Person</button>
            </div>
          </form>
        )}

        {/* ROLES */}
        {tab === "roles" && (
          <div className="card table-container">
            <h3>Company Roles</h3>
            <table className="table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Role</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((r) => (
                  <tr key={`role-${r.id}`}>
                    <td>
                      <Link to={`/company/${r.company_id}`}>{r.company_name || r.company?.name || `Company #${r.company_id}`}</Link>
                    </td>
                    <td>{titleCase(r.role)}</td>
                    <td>{r.start_date?.slice(0, 10) || "-"}</td>
                    <td>{r.end_date?.slice(0, 10) || "-"}</td>
                    <td>{r.end_date ? "Ended" : "Active"}</td>
                  </tr>
                ))}
                {!roles.length && (
                  <tr>
                    <td colSpan={5}>No company roles found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* DOCUMENTS */}
        {tab === "documents" && (
          <>
            {/* ✅ Expiry Warning */}
            {expiringCount > 0 && (
              <div
                className="card"
                style={{ padding: "12px 16px", marginBottom: "16px", background: "var(--warning-bg, #fff3cd)", border: "1px solid var(--warning-border, #ffc107)" }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 20 }}>⚠️</span>
                  <div>
                    <strong>{expiringCount}</strong> document{expiringCount !== 1 ? "s" : ""} expiring within 30 days
                  </div>
                </div>
              </div>
            )}

            <div className="card table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Kind</th>
                    <th>Open</th>
                    <th>Number</th>
                    <th>Issued</th>
                    <th>Expires</th>
                    <th>Status</th>
                    <th>Note</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((d) => {
                    const expired = d.expires_on && new Date(d.expires_on) < new Date();
                    const expiringSoon = d.expires_on && !expired && countExpiringDocs([d]) > 0;
                    const editing = editDoc && editDoc.id === d.id;
                    return (
                      <Fragment key={`doc-${d.id}`}>
                        <tr className={`${expired ? "row-expired" : expiringSoon ? "row-expiring-soon" : ""} ${editing ? "row--editing" : ""}`}>
                          <td>{d.kind}</td>
                          <td>
                            {d.path ? (
                              <button type="button" className="btn btn-link" onClick={() => openDocWithSignedUrl(d.id)}>
                                {d.path.toLowerCase().endsWith(".pdf") ? "Open PDF" : "Open"}
                              </button>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td>{d.identifier || "-"}</td>
                          <td>{d.issued_on?.slice(0, 10) || "-"}</td>
                          <td>
                            {d.expires_on?.slice(0, 10) || "-"}
                            {expiringSoon && <span style={{ marginLeft: 8, color: "var(--warning, #ffc107)" }}>⚠️</span>}
                          </td>
                          <td>{d.status || "-"}</td>
                          <td className="cell-ellipsis">{d.note || "-"}</td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {!editing && (
                              <>
                                <button type="button" onClick={() => startEditDocRow(d)}>
                                  Edit
                                </button>
                                <button type="button" className="secondary" onClick={() => removeDoc(d.id)} style={{ marginLeft: 4 }}>
                                  Delete
                                </button>
                              </>
                            )}
                          </td>
                        </tr>

                        {editing && (
                          <tr className="inline-editor-row">
                            <td colSpan={8}>
                              <form onSubmit={saveDocEdit}>
                                <div className="inline-editor">
                                  <label>
                                    <span>Kind</span>
                                    <select value={editDoc.kind} onChange={(e) => setEditDoc({ ...editDoc, kind: e.target.value })}>
                                      {effectiveDocKinds.map((o) => (
                                        <option key={o.key || o.value} value={o.key || o.value}>
                                          {o.label || o.key || o.value}
                                        </option>
                                      ))}
                                    </select>
                                  </label>

                                  <label className="span-2">
                                    <span>File</span>
                                    <div className="file-line">
                                      {editDoc.path && (
                                        <span className="file-chip" title={editDoc.path.split("/").pop()}>
                                          Current
                                        </span>
                                      )}
                                      <input type="file" accept="image/*,application/pdf" onChange={(e) => setEditDoc({ ...editDoc, file: e.target.files?.[0] || null })} />
                                    </div>
                                  </label>

                                  <label>
                                    <span>Number</span>
                                    <input value={editDoc.identifier || ""} onChange={(e) => setEditDoc({ ...editDoc, identifier: e.target.value })} />
                                  </label>

                                  <label>
                                    <span>Issued</span>
                                    <input type="date" value={editDoc.issued_on || ""} onChange={(e) => setEditDoc({ ...editDoc, issued_on: e.target.value })} />
                                  </label>

                                  <label>
                                    <span>Expires</span>
                                    <input type="date" value={editDoc.expires_on || ""} onChange={(e) => setEditDoc({ ...editDoc, expires_on: e.target.value })} />
                                  </label>

                                  <label>
                                    <span>Status</span>
                                    <select value={editDoc.status || ""} onChange={(e) => setEditDoc({ ...editDoc, status: e.target.value })}>
                                      <option value="">—</option>
                                      {meta.docStatuses.map((st) => (
                                        <option key={st} value={st}>
                                          {st}
                                        </option>
                                      ))}
                                    </select>
                                  </label>

                                  <label className="span-2">
                                    <span>Note</span>
                                    <input value={editDoc.note || ""} onChange={(e) => setEditDoc({ ...editDoc, note: e.target.value })} />
                                  </label>
                                </div>

                                <div className="inline-editor__actions">
                                  <button type="submit">Save</button>
                                  <button type="button" className="secondary" onClick={() => setEditDoc(null)}>
                                    Cancel
                                  </button>
                                </div>
                              </form>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {!docs.length && (
                    <tr>
                      <td colSpan={8}>No documents yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <form className="card form-section" onSubmit={addDoc}>
              <h3>Add document</h3>
              <div className="form-grid">
                <Field label="Kind">
                  <select value={newDoc.kind} onChange={(e) => setNewDoc({ ...newDoc, kind: e.target.value })}>
                    {effectiveDocKinds.map((o) => (
                      <option key={o.key || o.value} value={o.key || o.value}>
                        {o.label || o.key || o.value}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="File">
                  <input type="file" accept="image/*,application/pdf" onChange={(e) => setNewDoc({ ...newDoc, file: e.target.files?.[0] || null })} />
                </Field>

                <Field label="Identifier / number">
                  <input value={newDoc.identifier} onChange={(e) => setNewDoc({ ...newDoc, identifier: e.target.value })} />
                </Field>

                <Field label="Issued on">
                  <input type="date" value={newDoc.issued_on} onChange={(e) => setNewDoc({ ...newDoc, issued_on: e.target.value })} />
                </Field>

                <Field label="Expires on">
                  <input type="date" value={newDoc.expires_on} onChange={(e) => setNewDoc({ ...newDoc, expires_on: e.target.value })} />
                </Field>

                <Field label="Note">
                  <input value={newDoc.note} onChange={(e) => setNewDoc({ ...newDoc, note: e.target.value })} />
                </Field>
              </div>
              <div className="form-actions">
                <button type="submit">Add document</button>
              </div>
            </form>
          </>
        )}

        {/* ADDRESSES */}
        {tab === "addresses" && (
          <>
            <div className="card table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Address</th>
                    <th>Country</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {addresses.map((a) => {
                    const editing = editAddress && editAddress.id === a.id;
                    return (
                      <Fragment key={`addr-${a.id}`}>
                        <tr>
                          <td>{titleCase(a.type)}</td>
                          <td>{a.address_text}</td>
                          <td>{a.country_id ? countryById.get(String(a.country_id))?.name || a.country_id : ""}</td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {!editing && (
                              <>
                                <button type="button" onClick={() => startEditAddress(a)}>
                                  Edit
                                </button>
                                <button type="button" className="secondary" onClick={() => removeAddress(a.id)} style={{ marginLeft: 4 }}>
                                  Delete
                                </button>
                              </>
                            )}
                          </td>
                        </tr>

                        {editing && (
                          <tr className="inline-editor-row">
                            <td colSpan={4}>
                              <form onSubmit={saveAddressEdit}>
                                <div className="inline-editor">
                                  <label>
                                    <span>Type</span>
                                    <select value={editAddress.type} onChange={(e) => setEditAddress({ ...editAddress, type: e.target.value })}>
                                      <option value="residence">Residence</option>
                                      <option value="mailing">Mailing</option>
                                      <option value="business">Business</option>
                                      <option value="other">Other</option>
                                    </select>
                                  </label>

                                  <label>
                                    <span>Country</span>
                                    <CountrySelect value={editAddress.country_id} onChange={(val) => setEditAddress({ ...editAddress, country_id: val })} countries={countries} />
                                  </label>

                                  <label className="span-2">
                                    <span>Address</span>
                                    <input value={editAddress.address_text} onChange={(e) => setEditAddress({ ...editAddress, address_text: e.target.value })} />
                                  </label>
                                </div>

                                <div className="inline-editor__actions">
                                  <button type="submit">Save</button>
                                  <button type="button" className="secondary" onClick={() => setEditAddress(null)}>
                                    Cancel
                                  </button>
                                </div>
                              </form>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {!addresses.length && (
                    <tr>
                      <td colSpan={4}>No addresses</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <form className="card form-section" onSubmit={addAddress}>
              <h3>Add address</h3>
              <div className="form-grid">
                <Field label="Type">
                  <select value={newAddress.type} onChange={(e) => setNewAddress({ ...newAddress, type: e.target.value })}>
                    <option value="residence">Residence</option>
                    <option value="mailing">Mailing</option>
                    <option value="business">Business</option>
                    <option value="other">Other</option>
                  </select>
                </Field>

                <Field label="Country">
                  <CountrySelect value={newAddress.country_id} onChange={(val) => setNewAddress({ ...newAddress, country_id: val })} countries={countries} />
                </Field>
              </div>

              <Field label="Address">
                <input value={newAddress.address_text} onChange={(e) => setNewAddress({ ...newAddress, address_text: e.target.value })} />
              </Field>

              <div className="form-actions">
                <button type="submit">Add address</button>
              </div>
            </form>
          </>
        )}

        {/* NOTES */}
        {tab === "notes" && (
          <>
            <div className="card table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Note</th>
                    <th>Source</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {notes.map((n) => {
                    const editing = editingNoteId === n.id;
                    return (
                      <Fragment key={`note-${n.id}`}>
                        <tr>
                          <td className="cell-ellipsis">{n.note}</td>
                          <td>{n.source || "-"}</td>
                          <td>{n.created_at?.slice(0, 10) || "-"}</td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {!editing && (
                              <>
                                <button type="button" onClick={() => beginEditNote(n)}>
                                  Edit
                                </button>
                                <button type="button" className="secondary" onClick={() => removeNote(n.id)} style={{ marginLeft: 4 }}>
                                  Delete
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                        {editing && (
                          <tr className="inline-editor-row">
                            <td colSpan={4}>
                              <div className="inline-editor">
                                <label className="span-2">
                                  <span>Note</span>
                                  <input value={noteDraft.note} onChange={(e) => setNoteDraft({ ...noteDraft, note: e.target.value })} />
                                </label>
                                <label>
                                  <span>Source</span>
                                  <input value={noteDraft.source} onChange={(e) => setNoteDraft({ ...noteDraft, source: e.target.value })} />
                                </label>
                              </div>
                              <div className="inline-editor__actions">
                                <button type="button" onClick={saveNoteEdit}>
                                  Save
                                </button>
                                <button type="button" className="secondary" onClick={() => setEditingNoteId(null)}>
                                  Cancel
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {!notes.length && (
                    <tr>
                      <td colSpan={4}>No notes yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <form className="card form-section" onSubmit={addNote}>
              <h3>Add note</h3>
              <div className="form-grid">
                <Field label="Note">
                  <input value={newNote.note} onChange={(e) => setNewNote({ ...newNote, note: e.target.value })} />
                </Field>
                <Field label="Source">
                  <input value={newNote.source} onChange={(e) => setNewNote({ ...newNote, source: e.target.value })} />
                </Field>
              </div>
              <div className="form-actions">
                <button type="submit">Add note</button>
              </div>
            </form>
          </>
        )}
      </div>
    </ErrorBoundary>
  );
}
